/**
 * API Service Layer
 * Handles all communication with the Azure Functions backend.
 *
 * Features:
 *  - Request timeout handling
 *  - Exponential-backoff retry ONLY for idempotent GET calls
 *  - Custom error classes for callers to discriminate
 *
 * Notes:
 *  - We deliberately do NOT retry `complete` or `submit-test` at the client
 *    layer — the backend is idempotent (UPSERT guarded by Status='Completed'),
 *    but letting the client burn multiple round-trips during brief network
 *    blips masks real user-visible errors.
 */

import { logger } from '../lib/logger';
import { isAllowedOrigin, sendToParent } from '../lib/parentOrigins';

// API base URL from environment variable - required in production
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:7071/api';

const API_CONFIG = {
  DEFAULT_TIMEOUT_MS: 30000,

  MAX_RETRY_ATTEMPTS: 3,
  INITIAL_RETRY_DELAY_MS: 1000,
} as const;

export { API_CONFIG };

/**
 * Custom error types for better error handling
 */
export class APIError extends Error {
  constructor(
    message: string,
    public statusCode?: number,
    public isRetryable: boolean = false,
    public originalError?: unknown
  ) {
    super(message);
    this.name = 'APIError';
  }
}

export class NetworkError extends APIError {
  constructor(message: string = 'Network connection failed. Please check your internet connection.') {
    super(message, undefined, true);
    this.name = 'NetworkError';
  }
}

export class TimeoutError extends APIError {
  constructor(message: string = 'Request timed out. The server took too long to respond.') {
    super(message, 408, true);
    this.name = 'TimeoutError';
  }
}

export class RateLimitError extends APIError {
  constructor(message: string = 'Too many requests. Please wait a few minutes before trying again.') {
    super(message, 429, false);
    this.name = 'RateLimitError';
  }
}

export class ServerError extends APIError {
  constructor(message: string = 'Server error occurred. Please try again later.') {
    super(message, 500, true);
    this.name = 'ServerError';
  }
}

class APIService {
  private token: string | null = null;
  private pendingRefresh: Promise<string | null> | null = null;

  setToken(token: string) {
    this.token = token;
  }

  /**
   * Ask the parent portal for a fresh token (it calls MSAL acquireTokenSilent on
   * REQUEST_AUTH_TOKEN). The CIAM token lasts about an hour and this app only
   * received one on load, so long lesson sessions started failing every save with
   * 401 until the student reloaded. Resolves to the new token, or null if the
   * parent did not answer in time or replayed the same token. Concurrent callers
   * share one request.
   */
  private requestFreshToken(timeoutMs = 3000): Promise<string | null> {
    if (window.parent === window) return Promise.resolve(null);
    if (this.pendingRefresh) return this.pendingRefresh;
    const prior = this.token;
    this.pendingRefresh = new Promise<string | null>((resolve) => {
      const finish = (value: string | null) => {
        window.removeEventListener('message', listener);
        clearTimeout(timer);
        resolve(value);
      };
      const listener = (event: MessageEvent) => {
        if (!isAllowedOrigin(event.origin)) return;
        if (event.data?.type === 'AUTH_TOKEN' && event.data?.token) {
          finish(event.data.token !== prior ? event.data.token : null);
        }
      };
      window.addEventListener('message', listener);
      const timer = setTimeout(() => finish(null), timeoutMs);
      sendToParent({ type: 'REQUEST_AUTH_TOKEN' });
    }).finally(() => {
      this.pendingRefresh = null;
    });
    return this.pendingRefresh;
  }

  /**
   * Sleep utility for retry delays
   */
  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Calculate exponential backoff delay
   */
  private getRetryDelay(attemptNumber: number): number {
    return API_CONFIG.INITIAL_RETRY_DELAY_MS * Math.pow(2, attemptNumber - 1);
  }

  /**
   * Determine if error is retryable
   */
  private isRetryableError(error: unknown): boolean {
    if (error instanceof APIError) {
      return error.isRetryable;
    }
    if (error instanceof TypeError && error.message.includes('fetch')) {
      return true;
    }
    return false;
  }

  /**
   * Make HTTP request with timeout
   */
  private async fetchWithTimeout(
    url: string,
    options: RequestInit,
    timeoutMs: number = API_CONFIG.DEFAULT_TIMEOUT_MS
  ): Promise<Response> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      return response;
    } catch (error) {
      clearTimeout(timeoutId);

      if (error instanceof Error && error.name === 'AbortError') {
        throw new TimeoutError();
      }

      if (!navigator.onLine) {
        throw new NetworkError();
      }

      throw error;
    }
  }

  /**
   * Core request method with retry logic, timeout, and error handling.
   */
  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    enableRetry: boolean = true,
    authRetried: boolean = false
  ): Promise<T> {
    if (!navigator.onLine) {
      throw new NetworkError('You appear to be offline. Please check your internet connection.');
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>)
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    const url = `${API_BASE_URL}${endpoint}`;
    let lastError: unknown;

    const maxAttempts = enableRetry ? API_CONFIG.MAX_RETRY_ATTEMPTS : 1;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const response = await this.fetchWithTimeout(url, { ...options, headers });

        if (response.status === 401 && !authRetried) {
          // Expired token: get a fresh one from the portal and retry once with it.
          const fresh = await this.requestFreshToken();
          if (fresh) {
            this.token = fresh;
            return this.request<T>(endpoint, options, enableRetry, true);
          }
        }

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
          const errorMessage = errorData.message || errorData.error || `API error: ${response.status}`;

          if (response.status === 429) {
            throw new RateLimitError(errorMessage);
          } else if (response.status >= 500) {
            throw new ServerError(errorMessage);
          } else {
            throw new APIError(errorMessage, response.status, false);
          }
        }

        const data = await response.json();
        return data as T;
      } catch (error) {
        lastError = error;

        if (!this.isRetryableError(error) || attempt === maxAttempts) {
          break;
        }

        const delay = this.getRetryDelay(attempt);
        logger.warn(`Request failed (attempt ${attempt}/${maxAttempts}). Retrying in ${delay}ms.`);
        await this.sleep(delay);
      }
    }

    if (lastError instanceof APIError) {
      throw lastError;
    } else if (lastError instanceof Error) {
      throw new APIError(lastError.message, undefined, this.isRetryableError(lastError), lastError);
    } else {
      throw new APIError('Unknown error occurred', undefined, false, lastError);
    }
  }

  // -------------------------------------------------------------------------
  // Lesson Progress Methods
  // -------------------------------------------------------------------------

  async startLesson(lessonId: number): Promise<{ success: boolean; progressId: string }> {
    return this.request('/lesson/start', {
      method: 'POST',
      body: JSON.stringify({ lessonId })
    }, false);
  }

  async saveProgress(
    lessonId: number,
    responseData: Record<string, unknown>,
    timeSpentMinutes?: number
  ): Promise<{ success: boolean }> {
    return this.request('/lesson/save-progress', {
      method: 'POST',
      body: JSON.stringify({ lessonId, responseData, timeSpentMinutes })
    }, true);
  }

  /**
   * Complete a lesson. Server grades `knowledgeCheckAnswers` authoritatively —
   * any client-supplied `score` is ignored by the backend.
   */
  async completeLesson(
    lessonId: number,
    responseData: Record<string, unknown>,
    opts?: {
      knowledgeCheckAnswers?: Record<string, string | string[]>;
      timeSpentMinutes?: number;
    }
  ): Promise<{ success: boolean; score?: number; alreadyCompleted?: boolean }> {
    return this.request('/lesson/complete', {
      method: 'POST',
      body: JSON.stringify({
        lessonId,
        responseData,
        knowledgeCheckAnswers: opts?.knowledgeCheckAnswers,
        timeSpentMinutes: opts?.timeSpentMinutes,
      })
    }, false); // One-shot: don't retry client-side (server is idempotent).
  }

  /**
   * Submit a phase test or final exam. Server computes the authoritative score.
   */
  async submitTest(payload: {
    testType: 'phase' | 'final';
    audience?: 'teen' | 'adult';
    phaseId?: number;
    answers: Record<string, string | string[] | Record<string, string>>;
    startedAt?: string;
    timeSpentMinutes?: number;
  }): Promise<{
    success: boolean;
    score?: number;
    correctCount?: number;
    totalCount?: number;
    perQuestion?: Record<string, boolean>;
    alreadyCompleted?: boolean;
    lessonId?: number;
    completedAt?: string;
  }> {
    return this.request('/lesson/submit-test', {
      method: 'POST',
      body: JSON.stringify(payload)
    }, false);
  }

  async getProgress(lessonId: number): Promise<ProgressResponse> {
    return this.request(`/lesson/progress?lessonId=${lessonId}`, { method: 'GET' }, true);
  }

  async getAllProgress(): Promise<AllProgressResponse> {
    return this.request('/lesson/progress/all', { method: 'GET' }, true);
  }
}

/**
 * Type definitions
 */
export interface ProgressResponse {
  exists: boolean;
  progressId?: string;
  status: string;
  startedAt?: string | null;
  completedAt?: string | null;
  score?: number | null;
  responseData?: Record<string, any> | null;
  timeSpentMinutes?: number | null;
  feedbackFromTeacher?: string | null;
  message?: string;
}

export interface AllProgressResponse {
  lessons: {
    lessonId: number;
    progressId: string;
    status: string;
    startedAt: string | null;
    completedAt: string | null;
    score: number | null;
    timeSpentMinutes: number | null;
  }[];
}

// Export singleton instance
export const api = new APIService();
